import { defineStore } from 'pinia';
import { useUser } from './user';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import { tagOrderIndex, sortByTagOrder } from '@lib/tag-order';
import { computed, type ComputedRef } from 'vue';
import type { ComponentTagCategoryDto, ComponentTagDto } from '@/ApiClient';

export const useComponentTags = defineStore('component-tags', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.componentTag_GetCategories,
    dto: 'ComponentTagCategoryDto',
    update: api.componentTag_UpdateCategory,
    delete: api.componentTag_DeleteCategory,
    signalR: 'ComponentTagUpdate',
  });
  const { loaded } = globalStoreBase;

  const any: ComputedRef<boolean> = computed(() => {
    return Boolean(loaded.value.length && loaded.value.find((tagGroup) => tagGroup.tags && tagGroup.tags.length > 0));
  });

  const sorted: ComputedRef<ComponentTagCategoryDto[]> = computed(() => {
    return loaded.value
      .sort((a, b) => a.name?.localeCompare(b.name || '') || 0)
      .map((tagGroup) => ({
        ...tagGroup,
        tags: tagGroup.tags?.sort((a, b) => a.name?.localeCompare(b.name || '') || 0),
      }));
  });

  const baseCategory: ComputedRef<ComponentTagCategoryDto> = computed(() => {
    return loaded.value[0];
  });

  const byId: ComputedRef<Record<number, ComponentTagDto>> = computed(() => {
    const allTags = loaded.value.flatMap((category) => category.tags ?? []);
    return Object.fromEntries(allTags.map((tag) => [tag.id, tag]));
  });

  async function addCategory(name: string, key: string, baseCategory = false) {
    const brandId = useUser().brandId;

    const category = {
      name,
      key,
      baseCategory,
    };

    loaded.value = await api.componentTag_AddCategory(brandId, category);
  }

  async function addTag(name: string, color: string, tagCategoryId: number, externalCode?: string) {
    const brandId = useUser().brandId;

    const componentTagWithTagCategory = {
      name,
      color,
      externalCode,
      tagCategoryId,
    };

    const componentTag = await api.componentTag_AddTag(brandId, componentTagWithTagCategory);

    const categoryIndex = loaded.value.findIndex((category) => category.id === tagCategoryId);

    if (categoryIndex > -1) {
      if (!loaded.value[categoryIndex].tags) loaded.value[categoryIndex].tags = [];

      loaded.value[categoryIndex].tags?.push(componentTag);
      loaded.value[categoryIndex].tags?.sort((a, b) => a.name?.localeCompare(b.name || '') || 0);
    }

    return componentTag;
  }

  async function updateTag(componentTagId: number, name: string, color: string, externalCode?: string) {
    const brandId = useUser().brandId;

    const componentTag = await api.componentTag_UpdateTag(brandId, componentTagId, { name, color, externalCode });

    const categoryIndex = loaded.value.findIndex((category) => category.id === componentTag.tagCategoryId);
    if (categoryIndex > -1) {
      const tagIndexInCategory = loaded.value[categoryIndex].tags?.findIndex((tag) => tag.id === componentTag.id);
      if (tagIndexInCategory !== undefined && tagIndexInCategory > -1) {
        loaded.value[categoryIndex].tags?.splice(tagIndexInCategory, 1, componentTag);
        loaded.value[categoryIndex].tags?.sort((a, b) => a.name?.localeCompare(b.name || '') || 0);
      }
    }

    return componentTag;
  }

  async function deleteTag(tagId: number) {
    const brandId = useUser().brandId;

    await api.componentTag_DeleteTag(brandId, tagId);

    const category = loaded.value.find((c) => c.tags?.some((t) => t.id == tagId));
    if (category) {
      const index = category.tags!.findIndex((t) => t.id == tagId);
      category.tags!.splice(index, 1);
    }
  }

  function idToTag(id: number) {
    for (const category of loaded.value) {
      if (!category.tags) continue;

      for (const componentTag of category.tags) {
        if (componentTag.id === id) return componentTag;
      }
    }
  }

  function idsToTags(ids: number[]) {
    const tags: ComponentTagDto[] = [];

    for (const id of ids) {
      const componentTag = idToTag(id);

      if (componentTag) tags.push(componentTag);
    }

    return tags;
  }

  // One palette-order Map per store: pill components sort tag ids through this instead of every
  // cell instance building its own Map over the whole palette.
  const orderIndexById: ComputedRef<Map<number, number>> = computed(() => tagOrderIndex(sorted.value));

  function sortByOrder<T extends { id: number }>(tags: T[]): T[] {
    return sortByTagOrder(tags, orderIndexById.value);
  }

  return {
    ...globalStoreBase,
    any,
    sorted,
    orderIndexById,
    sortByOrder,
    loaded,
    baseCategory,
    byId,
    addCategory,
    addTag,
    addNewTag: addTag,
    updateTag,
    deleteTag,
    idToTag,
    idsToTags,
  };
});
